import { requestJson, responseArray, responseRecord } from './http';

export type SubscriptionLane = 'claude' | 'chatgpt_codex' | 'antigravity_gemini';
export type ConnectionState =
  | 'not_connected'
  | 'signing_in'
  | 'stale'
  | 'unverifiable'
  | 'expired'
  | 'connected';

export interface QuotaWindow {
  kind: string;
  percent_used: number;
  resets_at: string | null;
}

export interface ConnectionRow {
  lane: SubscriptionLane;
  state: ConnectionState;
  checked_at: string | null;
  enabled: boolean;
  node: { device_id: string; label: string } | null;
  quota: { windows: QuotaWindow[] } | null;
  quota_status: 'measured' | 'not_measured';
  last_known_state: ConnectionState | null;
  last_known_age_seconds: number | null;
  pairing_required: boolean;
  sign_in: { attempt_id: string; expires_at: string } | null;
}

export interface SignInRelay {
  verification_uri: string;
  user_code: string;
  expires_at: string;
}

export interface SignInAttempt {
  attempt_id: string;
  lane: SubscriptionLane;
  status: 'queued' | 'in_progress' | 'awaiting_user' | 'finished' | 'failed' | 'expired' | 'cancelled';
  created_at: string;
  expires_at: string;
  relay: SignInRelay | null;
}

export interface SignInStartResponse {
  attempt: SignInAttempt;
  connection: ConnectionRow;
  duplicate: boolean;
}

const VERIFICATION_HOSTS: Record<SubscriptionLane, readonly string[]> = {
  claude: ['claude.ai', 'claude.com', 'console.anthropic.com'],
  chatgpt_codex: ['auth.openai.com'],
  antigravity_gemini: ['accounts.google.com', 'antigravity.google', 'antigravity.google.com'],
};

/** Re-check the server-validated relay before creating an external link. */
export function allowlistedVerificationUri(lane: SubscriptionLane, candidate: string): string | null {
  try {
    const url = new URL(candidate);
    const host = url.hostname.toLowerCase();
    if (
      url.protocol !== 'https:'
      || !VERIFICATION_HOSTS[lane].includes(host)
      || url.username
      || url.password
      || url.port
      || url.search
      || url.hash
      || !/^[\x00-\x7F]+$/.test(host)
      || host.startsWith('xn--')
    ) {
      return null;
    }
    return url.href;
  } catch {
    return null;
  }
}

const noFields = { method: 'POST', body: JSON.stringify({}) };

export async function listConnections(): Promise<ConnectionRow[]> {
  const payload = await requestJson('/v1/connections');
  return responseArray(payload, 'connections') as ConnectionRow[];
}

export async function startConnectionSignIn(lane: SubscriptionLane): Promise<SignInStartResponse> {
  const payload = await requestJson('/v1/connections/' + encodeURIComponent(lane) + '/sign-in', noFields);
  const record = responseRecord(payload);
  return {
    attempt: responseRecord(record, 'attempt') as unknown as SignInAttempt,
    connection: responseRecord(record, 'connection') as unknown as ConnectionRow,
    duplicate: record.duplicate === true,
  };
}

export async function readConnectionSignIn(
  lane: SubscriptionLane,
  attemptId: string,
): Promise<SignInAttempt> {
  const payload = await requestJson(
    '/v1/connections/' + encodeURIComponent(lane) + '/sign-in/' + encodeURIComponent(attemptId),
  );
  return responseRecord(payload, 'attempt') as unknown as SignInAttempt;
}

export async function signOutOfConnection(lane: SubscriptionLane): Promise<Record<string, unknown>> {
  const payload = await requestJson('/v1/connections/' + encodeURIComponent(lane) + '/sign-out', noFields);
  return responseRecord(payload);
}

export async function disconnectConnection(lane: SubscriptionLane): Promise<ConnectionRow> {
  const payload = await requestJson('/v1/connections/' + encodeURIComponent(lane) + '/disconnect', noFields);
  return responseRecord(payload, 'connection') as unknown as ConnectionRow;
}

export async function reconnectConnection(lane: SubscriptionLane): Promise<ConnectionRow> {
  const payload = await requestJson('/v1/connections/' + encodeURIComponent(lane) + '/reconnect', noFields);
  return responseRecord(payload, 'connection') as unknown as ConnectionRow;
}
